import React from 'react';
import { Helmet } from 'react-helmet';
const SITE_URL = 'https://medicolegal-psychiatry.co.uk';
const DEFAULT_SOCIAL_IMAGE = 'https://uploadthingy.s3.us-west-1.amazonaws.com/u9Nr778koLscH9uY6kZjEn/01._Hero_Image.png';
const DEFAULT_SOCIAL_ALT = 'Medicolegal Psychiatry, independent psychiatric expert witness reports';
export type JsonLd = Record<string, unknown> | Array<Record<string, unknown>>;
type SEOProps = {
  /** Unique <title> for this page */
  title: string;
  /** Unique meta description for this page */
  description: string;
  /** Route path, e.g. '/about'. Use '/' for the homepage. */
  path: string;
  keywords?: string[];
  image?: string;
  imageAlt?: string;
  type?: 'website' | 'article';
  /** Page-specific JSON-LD, in addition to the automatic BreadcrumbList */
  schema?: JsonLd;
  noindex?: boolean;
};

// Breadcrumb names for every route below the homepage.
const breadcrumbLabels: Record<string, string> = {
  '/about': 'About Dr Imtiaz Syed',
  '/services': 'Services',
  '/services/mental-capacity': 'Mental Capacity Assessments',
  '/services/trauma-ptsd': 'PTSD and Complex Trauma',
  '/services/neurodevelopmental': 'ADHD and Neurodevelopmental Assessments',
  '/services/forensic-risk': 'Criminal Reports and Fitness to Plead',
  '/services/specialist': 'Immigration, Employment and Family Reports',
  '/services/clinical-negligence': 'Clinical Negligence and Personal Injury',
  '/faq': 'FAQs',
  '/contact': 'Contact',
  '/sitemap': 'Sitemap',
  '/policies/privacy': 'Privacy and Cookie Policy',
  '/policies/terms': 'Terms and Conditions',
  '/policies/cancellation': 'Cancellation Policy'
};
function getBreadcrumbSchema(path: string) {
  if (path === '/' || !breadcrumbLabels[path]) return null;
  const items: Array<Record<string, unknown>> = [{
    '@type': 'ListItem',
    position: 1,
    name: 'Home',
    item: SITE_URL
  }];
  // Service detail pages sit below the services index.
  if (path.startsWith('/services/')) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: 'Services',
      item: `${SITE_URL}/services`
    });
  }
  items.push({
    '@type': 'ListItem',
    position: items.length + 1,
    name: breadcrumbLabels[path],
    item: `${SITE_URL}${path}`
  });
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
  };
}
export default function SEO({
  title,
  description,
  path,
  keywords = [],
  image = DEFAULT_SOCIAL_IMAGE,
  imageAlt = DEFAULT_SOCIAL_ALT,
  type = 'website',
  schema,
  noindex = false
}: SEOProps) {
  const canonicalUrl = path === '/' ? SITE_URL : `${SITE_URL}${path}`;
  const breadcrumbSchema = getBreadcrumbSchema(path);
  return <Helmet data-id="element-388" data-mp="3n0py7ewaunz3">
      <title data-id="element-389" data-mp="3n181gvhdq2gi">{title}</title>
      <meta name="description" content={description} data-id="element-390" data-mp="3n3ubr2vnmw2f" />
      <meta name="robots" content={noindex ? 'noindex, follow' : 'index, follow'} data-id="element-391" data-mp="2fp8yeg9uuwue" />
      {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} data-id="element-392" data-mp="2bop4rmohqbd0" />}
      <link rel="canonical" href={canonicalUrl} data-id="element-393" data-mp="r5v063e4zesj" />
      {/* Open Graph */}
      <meta property="og:type" content={type} data-id="element-394" data-mp="v5dnt6xce3z2" />
      <meta property="og:site_name" content="Medicolegal Psychiatry" data-id="element-395" data-mp="v42j94w2os6w" />
      <meta property="og:locale" content="en_GB" data-id="element-396" data-mp="104ghjv7vgnrq" />
      <meta property="og:title" content={title} data-id="element-397" data-mp="14vyjomlmsuea" />
      <meta property="og:description" content={description} data-id="element-398" data-mp="14wqehv7emo3i" />
      <meta property="og:url" content={canonicalUrl} data-id="element-399" data-mp="19vbd5q70czpu" />
      <meta property="og:image" content={image} data-id="element-400" data-mp="19uryqjgkgtgk" />
      <meta property="og:image:alt" content={imageAlt} data-id="element-401" data-mp="1etmo6491hg9n" />
      {/* Twitter / X card */}
      <meta name="twitter:card" content="summary_large_image" data-id="element-402" data-mp="1ivzk60xzhzt8" />
      <meta name="twitter:title" content={title} data-id="element-403" data-mp="1itn3rs7vjxia" />
      <meta name="twitter:description" content={description} data-id="element-404" data-mp="1nv2otka1y40o" />
      <meta name="twitter:image" content={image} data-id="element-405" data-mp="1nsgi1muzjsji" />
      <meta name="twitter:image:alt" content={imageAlt} data-id="element-406" data-mp="3ko5tvlfcon4s" />
      {schema && <script type="application/ld+json" data-id="element-407" data-mp="3plnzb1u5sk81">{JSON.stringify(schema)}</script>}
      {breadcrumbSchema && <script type="application/ld+json" data-id="element-408" data-mp="2uazizum7vzv2">
          {JSON.stringify(breadcrumbSchema)}
        </script>}
    </Helmet>;
}
export const siteUrl = SITE_URL;